import $ from 'jquery';
import * as bootstrap from 'bootstrap';

import QuarryApi from './quarry-api';

/**
 * "New Job Log" screen (quarry/job_log_view.html).
 * No-ops when its DOM root is absent, mirroring additional-tipping.js, so
 * this file can safely ship inside the shared `main` bundle.
 */
const QuarryLog = () => {
	const root = document.getElementById('quarryJobLog');

	if (!root) {
		return;
	}

	const listEl = document.getElementById('quarryJobLogList');
	const emptyEl = document.getElementById('quarryJobLogEmpty');
	const alertEl = document.getElementById('quarryJobLogAlert');

	const facility = root.getAttribute('data-facility') || '';
	const date = root.getAttribute('data-date') || '';
	const overviewUrlTemplate = root.getAttribute('data-overview-url-template') || '';

	const allocateModalEl = document.getElementById('quarryAllocateModal');
	const allocateConfirmBtn = document.getElementById('quarryAllocateConfirm');
	const allocateErrorEl = document.getElementById('quarryAllocateError');

	const addTruckModalEl = document.getElementById('quarryAddTruckModal');
	const addTruckConfirmBtn = document.getElementById('quarryAddTruckConfirm');
	const addTruckErrorEl = document.getElementById('quarryAddTruckError');
	const addTruckTypeSelect = document.getElementById('quarryAddTruckType');
	const addTruckSizeSelect = document.getElementById('quarryAddTruckSize');
	const addTruckRegoInput = document.getElementById('quarryAddTruckRego');

	let truckTypes = [];
	// jobsById keeps an in-memory copy of each job's rego tiles so we can
	// patch a single tile / job card in place after a successful action
	// instead of re-fetching + re-rendering the whole list.
	const jobsById = {};

	const escapeHtml = (value) => {
		return String(value === undefined || value === null ? '' : value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	};

	const overviewUrl = (jobId) => {
		if (!overviewUrlTemplate) {
			return '#';
		}

		return overviewUrlTemplate.replace(/0\/?$/, `${jobId}/`);
	};

	const statusModifier = (status) => {
		switch (status) {
			case 'PEN':
				return 'pending';
			case 'PAU':
				return 'paused';
			case 'UNA':
				return 'noallocations';
			default:
				return null;
		}
	};

	const showAlert = (message) => {
		if (!alertEl) {
			return;
		}

		alertEl.textContent = message;
		alertEl.classList.remove('d-none');
	};

	const hideAlert = () => {
		if (!alertEl) {
			return;
		}

		alertEl.classList.add('d-none');
		alertEl.textContent = '';
	};

	const sortRegos = (regos) => {
		return regos.slice().sort((a, b) => {
			if (b.loads !== a.loads) {
				return b.loads - a.loads;
			}

			return a.registration.localeCompare(b.registration);
		});
	};

	const renderRegoTile = (job, rego) => {
		const hasLoads = rego.loads > 0;
		const loadsLabel = `${rego.loads} LOAD${rego.loads === 1 ? '' : 'S'}`;

		return `
			<button
					type="button"
					class="quarry-rego-tile ${hasLoads ? 'quarry-rego-tile--active' : 'quarry-rego-tile--empty'}"
					data-bs-toggle="modal"
					data-bs-target="#quarryAllocateModal"
					data-job-id="${job.id}"
					data-client="${escapeHtml(job.client)}"
					data-job-name="${escapeHtml(job.job_name)}"
					data-registration="${escapeHtml(rego.registration)}"
			>
				<span class="quarry-rego-tile__rego">${escapeHtml(rego.registration)}</span>
				<span class="quarry-rego-tile__loads">${loadsLabel}</span>
			</button>
		`;
	};

	const renderAddTruckTile = (job) => {
		return `
			<button
					type="button"
					class="quarry-rego-tile quarry-rego-tile--add"
					data-bs-toggle="modal"
					data-bs-target="#quarryAddTruckModal"
					data-job-id="${job.id}"
					data-job-name="${escapeHtml(job.job_name)}"
			>
				Add Truck
			</button>
		`;
	};

	const renderJobRegos = (job) => {
		const regosHtml = sortRegos(job.regos).map((rego) => renderRegoTile(job, rego)).join('');

		return `${regosHtml}${renderAddTruckTile(job)}`;
	};

	const renderJobCard = (job) => {
		const modifier = statusModifier(job.status);
		const statusBadge = modifier
			? `<span class="job-status job-status--${modifier} quarry-job-card__status">${escapeHtml(job.status_display)}</span>`
			: '';

		return `
			<div class="quarry-job-card" data-job-id="${job.id}">
				<div class="quarry-job-card__header">
					<div class="quarry-job-card__col">
						<span class="quarry-job-card__label">Company Name</span>
						<span class="quarry-job-card__value">${escapeHtml(job.client)}</span>
					</div>
					<div class="quarry-job-card__col">
						<span class="quarry-job-card__label">Job Name</span>
						<span class="quarry-job-card__value">
							${escapeHtml(job.job_name)}${statusBadge}
						</span>
					</div>
					<a href="${overviewUrl(job.id)}" class="quarry-job-card__overview-btn">
						Job Log Overview
					</a>
				</div>
				<div class="quarry-job-card__body">
					<span class="quarry-job-card__regos-label">Select Vehicle Registration Number</span>
					<div class="quarry-job-card__regos-grid" data-job-id="${job.id}">
						${renderJobRegos(job)}
					</div>
				</div>
			</div>
		`;
	};

	const renderJobs = (jobs) => {
		if (!jobs.length) {
			listEl.innerHTML = '';
			emptyEl.classList.remove('d-none');

			return;
		}

		emptyEl.classList.add('d-none');
		listEl.innerHTML = jobs.map((job) => renderJobCard(job)).join('');
	};

	const rerenderJobRegos = (jobId) => {
		const job = jobsById[jobId];
		const gridEl = listEl.querySelector(`.quarry-job-card__regos-grid[data-job-id="${jobId}"]`);

		if (!job || !gridEl) {
			return;
		}

		gridEl.innerHTML = renderJobRegos(job);
	};

	const populateTruckTypeSelect = () => {
		if (!addTruckTypeSelect) {
			return;
		}

		const options = truckTypes.map((truck) => `<option value="${truck.key}" data-size="${truck.size}">${escapeHtml(truck.name)}</option>`).join('');

		addTruckTypeSelect.innerHTML = `<option value="">Select a truck</option>${options}`;
	};

	const populateTruckSizeSelect = (defaultSize) => {
		if (!addTruckSizeSelect) {
			return;
		}

		// Use the truck sizes defined in the system (JobAdditionalTipping.TRUCK_SIZES,
		// delivered via truck_types[].size) rather than an arbitrary 1-30 m³ range.
		const sizes = [...new Set(truckTypes.map((t) => Math.round(t.size)).filter((s) => s > 0))]
			.sort((a, b) => a - b);

		const rounded = defaultSize ? Math.round(defaultSize) : null;

		if (rounded && sizes.indexOf(rounded) === -1) {
			sizes.push(rounded);
			sizes.sort((a, b) => a - b);
		}

		addTruckSizeSelect.innerHTML = sizes
			.map((size) => `<option value="${size}" ${rounded === size ? 'selected' : ''}>${size} m&sup3;</option>`)
			.join('');
	};

	const fetchAndRender = async () => {
		hideAlert();

		const { body, error } = await QuarryApi.getJobLog(facility, date, this);

		if (error || !body) {
			showAlert('Unable to load the job log. Please refresh and try again.');

			return;
		}

		truckTypes = body.truck_types || [];
		populateTruckTypeSelect();

		(body.jobs || []).forEach((job) => {
			jobsById[job.id] = job;
		});

		renderJobs(body.jobs || []);
	};

	// Allocate ("tap to log a load") modal
	if (allocateModalEl) {
		allocateModalEl.addEventListener('show.bs.modal', (e) => {
			const button = e.relatedTarget;

			if (!button) {
				return;
			}

			allocateErrorEl.classList.add('d-none');

			document.getElementById('quarryAllocateClient').textContent = button.getAttribute('data-client');
			document.getElementById('quarryAllocateJobName').textContent = button.getAttribute('data-job-name');
			document.getElementById('quarryAllocateRego').textContent = button.getAttribute('data-registration');

			allocateConfirmBtn.setAttribute('data-job-id', button.getAttribute('data-job-id'));
			allocateConfirmBtn.setAttribute('data-registration', button.getAttribute('data-registration'));
		});

		allocateConfirmBtn.addEventListener('click', async () => {
			const jobId = allocateConfirmBtn.getAttribute('data-job-id');
			const registration = allocateConfirmBtn.getAttribute('data-registration');

			if (!jobId || !registration || allocateConfirmBtn.disabled) {
				return;
			}

			allocateErrorEl.classList.add('d-none');
			allocateConfirmBtn.setAttribute('disabled', 'disabled');
			$(allocateConfirmBtn).children('.fa-spinner').show();

			const { body, error } = await QuarryApi.createEntry(jobId, { registration }, this);

			$(allocateConfirmBtn).children('.fa-spinner').hide();
			allocateConfirmBtn.removeAttribute('disabled');

			if (error || !body) {
				const message = (error && error.responseJSON && (error.responseJSON.detail || error.responseJSON.registration))
					|| 'Unable to log this load. Please try again.';

				allocateErrorEl.textContent = Array.isArray(message) ? message[0] : message;
				allocateErrorEl.classList.remove('d-none');

				return;
			}

			const job = jobsById[jobId];

			if (job) {
				const existing = job.regos.find((rego) => rego.registration === registration);

				if (existing) {
					existing.loads += 1;
				} else {
					job.regos.push({
						registration,
						truck_type: body.truck_type,
						truck_type_display: body.truck_type_display,
						truck_size: body.truck_size,
						loads: 1
					});
				}

				rerenderJobRegos(jobId);
			}

			const modal = bootstrap.Modal.getInstance(allocateModalEl);

			if (modal) {
				modal.hide();
			}
		});
	}

	// Add Truck modal
	if (addTruckModalEl) {
		addTruckModalEl.addEventListener('show.bs.modal', (e) => {
			const button = e.relatedTarget;

			if (!button) {
				return;
			}

			addTruckErrorEl.classList.add('d-none');
			addTruckRegoInput.value = '';

			document.getElementById('quarryAddTruckJobName').textContent = button.getAttribute('data-job-name');

			addTruckConfirmBtn.setAttribute('data-job-id', button.getAttribute('data-job-id'));

			populateTruckTypeSelect();
			populateTruckSizeSelect(truckTypes.length ? truckTypes[0].size : null);
		});

		addTruckTypeSelect.addEventListener('change', () => {
			const selected = addTruckTypeSelect.options[addTruckTypeSelect.selectedIndex];
			const size = selected ? parseFloat(selected.getAttribute('data-size')) : null;

			populateTruckSizeSelect(size);
		});

		addTruckConfirmBtn.addEventListener('click', async () => {
			const jobId = addTruckConfirmBtn.getAttribute('data-job-id');
			const registration = addTruckRegoInput.value.trim();
			const truckType = addTruckTypeSelect.value;
			const truckSize = parseFloat(addTruckSizeSelect.value) || 0;

			if (!jobId || addTruckConfirmBtn.disabled) {
				return;
			}

			if (!registration || !truckType) {
				addTruckErrorEl.textContent = 'Registration number and type of truck are required.';
				addTruckErrorEl.classList.remove('d-none');

				return;
			}

			addTruckErrorEl.classList.add('d-none');
			addTruckConfirmBtn.setAttribute('disabled', 'disabled');
			$(addTruckConfirmBtn).children('.fa-spinner').show();

			const { body, error } = await QuarryApi.addTruck(jobId, {
				registration,
				truck_type: truckType,
				truck_size: truckSize
			}, this);

			$(addTruckConfirmBtn).children('.fa-spinner').hide();
			addTruckConfirmBtn.removeAttribute('disabled');

			if (error || !body) {
				const errorBody = error && error.responseJSON;
				const message = (errorBody && (errorBody.registration || errorBody.truck_type || errorBody.detail))
					|| 'Unable to add this truck. Please try again.';

				addTruckErrorEl.textContent = Array.isArray(message) ? message[0] : message;
				addTruckErrorEl.classList.remove('d-none');

				return;
			}

			const job = jobsById[jobId];

			if (job) {
				job.regos.push(body);
				rerenderJobRegos(jobId);
			}

			const modal = bootstrap.Modal.getInstance(addTruckModalEl);

			if (modal) {
				modal.hide();
			}
		});
	}

	fetchAndRender();
};

export default QuarryLog;
